import {
  useEffect,
  useRef,
  useImperativeHandle,
  useState,
  forwardRef,
  useMemo
} from "react"
import ApexCharts from "apexcharts"
import type { ApexOptions } from "apexcharts"
import "./BarChart.css"

interface Series {
  name: string
  data: number[]
}

interface BarChartProps {
  categories: string[]
  series: Series[]
  title?: string
  height?: number
  yAxisLabel?: string
  xAxisLabel?: string
  yAxisMin?: number
  yAxisMax?: number
  horizontal?: boolean
  stacked?: boolean
  colors?: string[]
  goalLine?: number
  goalLabel?: string
  goalLabelOffsetX?: number
  goalLabelOffsetY?: number
  goalLabelOrientation?: "horizontal" | "vertical"
  goalLabelPosition?: "top" | "bottom" | "left" | "right"
  fontFamily?: string
  showToolbar?: boolean
  legendPosition?: "top" | "bottom" | "left" | "right"
  legendAlign?: "left" | "center" | "right"
  legendFloating?: boolean
  legendOffsetX?: number
  legendOffsetY?: number
  legendFontSize?: string
  legendFontWeight?: number | string
  legendFontColor?: string
  showLegend?: boolean
  showLegendForSingleSeries?: boolean
  dataLabelsEnabled?: boolean
  dataLabelsFontWeight?: string
  dataLabelsFontSize?: string
  dataLabelsOffsetX?: number
  dataLabelsOffsetY?: number
  dataLabelsColor?: string[]
  dataLabelSuffix?: string
  exportFilename?: string
  plotOptionsBar?: NonNullable<ApexOptions["plotOptions"]>["bar"]
  columnWidth?: string
  xAxisMin?: number
  xAxisMax?: number
  xAxisTickAmount?: number
  yAxisTickAmount?: number
  xAxisShow?: boolean
  xAxisReserveSpace?: boolean
  xAxisMultiLine?: boolean
  showXAxisBorder?: boolean
  yAxisShow?: boolean
  yAxisWidth?: number
  yAxisDecimals?: number
  showGrid?: boolean
  decimals?: number
  noDataMessage?: string
  isLoading?: boolean
  categoryLabelsInside?: boolean
  zeroStateTextColor?: string
  gridPadding?: { top?: number; right?: number; bottom?: number; left?: number }
  xAxisLabelRotate?: number
  xAxisLabelRotateAlways?: boolean
  xAxisLabelMinHeight?: number
  xAxisLabelMaxHeight?: number
  xAxisLabelOffsetX?: number
  xAxisLabelOffsetY?: number
  xAxisHideOverlappingLabels?: boolean
  xAxisTrim?: boolean
  yAxisLabelOffsetX?: number
  tooltipCounts?: number[]
  tooltipCustom?: (opts: { series: number[][], seriesIndex: number, dataPointIndex: number, w: any }) => string
  tapTooltipOnTouch?: boolean
  showlabelsForBars?: boolean
}

const DEFAULT_COLORS = [
  "#0072B2",
  "#009E73",
  "#D55E00",
  "#CC79A7",
  "#F0E442"
]

function detectTouchDevice() {
  if (typeof window === "undefined") return false

  return (
    window.matchMedia("(hover: none), (pointer: coarse)").matches ||
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0
  )
}

const BarChart = forwardRef<any, BarChartProps>((props, ref) => {

  const {
    categories = [],
    series = [],
    title,
    height = 350,
    horizontal = false,
    stacked = false,
    colors = DEFAULT_COLORS,
    goalLine,
    goalLabel = "Goal",
    goalLabelOffsetX = 0,
    goalLabelOffsetY = 0,
    goalLabelOrientation,
    goalLabelPosition,
    fontFamily = "Helvetica, Arial, sans-serif",
    showToolbar = true,
    legendPosition = "top",
    legendAlign = "center",
    legendFloating = false,
    legendOffsetX = 0,
    legendOffsetY = 0,
    legendFontSize = "14px",
    legendFontWeight = 400,
    legendFontColor,
    showLegend,
    showLegendForSingleSeries = false,
    xAxisLabel = "",
    yAxisLabel = "",
    yAxisMin,
    yAxisMax,
    xAxisShow = true,
    xAxisReserveSpace = false,
    showXAxisBorder = true,
    xAxisMultiLine = false,
    xAxisMin,
    xAxisMax,
    xAxisTickAmount,
    yAxisTickAmount,
    yAxisShow = true,
    yAxisWidth,
    yAxisDecimals = 0,
    showGrid = true,
    decimals = 0,
    dataLabelsEnabled = true,
    dataLabelsColor,
    dataLabelsOffsetX = 0,
    dataLabelsOffsetY = 0,
    dataLabelsFontWeight = "600",
    dataLabelsFontSize = "12px",
    categoryLabelsInside = false,
    exportFilename,
    zeroStateTextColor = "#000",
    noDataMessage = "No data available",
    isLoading = false,
    columnWidth = "60",
    plotOptionsBar,
    dataLabelSuffix = "",
    gridPadding,
    xAxisLabelRotate = 0,
    xAxisLabelRotateAlways = false,
    xAxisLabelMinHeight,
    xAxisLabelMaxHeight,
    xAxisLabelOffsetX = 0,
    xAxisLabelOffsetY = 0,
    xAxisHideOverlappingLabels = true,
    xAxisTrim = false,
    yAxisLabelOffsetX = 0,
    tooltipCounts = [],
    tooltipCustom,
    tapTooltipOnTouch = false,
    showlabelsForBars=false
  } = props

  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<ApexCharts | null>(null)

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  )
  const [isTouchDevice, setIsTouchDevice] = useState(detectTouchDevice())
  const [tapTooltip, setTapTooltip] = useState<{
    visible: boolean
    x: number
    y: number
    html: string
    pointKey: string
  }>({
    visible: false,
    x: 0,
    y: 0,
    html: "",
    pointKey: ""
  })

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsTouchDevice(detectTouchDevice())
    }
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    if (!tapTooltip.visible) return

    const handlePointerDown = (event: PointerEvent) => {
      if (!chartRef.current?.contains(event.target as Node)) {
        setTapTooltip((current) => ({ ...current, visible: false }))
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [tapTooltip.visible])
  useImperativeHandle(ref, () => ({
    exportToCSV: () => {
      const header = ["Category", ...series.map((s) => s.name)].join(",")
      const rows = categories.map((cat, i) => {
        const cleanCat = String(cat).replace(/\n/g, " ")
        const values = series.map((s) => s.data[i] ?? "")
        return [cleanCat, ...values].join(",")
      })
      const csv = [header, ...rows].join("\n")
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `${exportFilename || "chart"}.csv`
      link.click()
      URL.revokeObjectURL(url)
    },
    exportToSVG: () => (chartInstance.current as any)?.exportToSVG()
  }))
  const safeSeries = series.map((s) => ({
    ...s,
    data: s.data.map((v) => Number(v))
  }))

  const getTooltipHtml = (
    chartSeries: number[][],
    seriesIndex: number,
    dataPointIndex: number,
    w: any
  ) => {
    if (horizontal && tooltipCounts.length > 0) {
      const value = chartSeries[seriesIndex]?.[dataPointIndex]
      const category = w.globals.labels[dataPointIndex] || ""
      const color = w.config.colors[0]
      const count = tooltipCounts[dataPointIndex] ?? 0
      const roundedPct = Math.round((Number(value) || 0) * 10) / 10
      const pctStr = (Math.abs(roundedPct - Math.round(roundedPct)) < 1e-9)
        ? `${Math.round(roundedPct)}%`
        : `${roundedPct.toFixed(1)}%`

      return `<div style="background: ${color}; color: #fff; padding: 6px 12px; border-radius: 4px; font-family: Poppins, Segoe UI, sans-serif; font-size: 13px; font-weight: 500;">
              ${category}: ${count} (${pctStr})
            </div>`
    }

    const value = chartSeries[seriesIndex]?.[dataPointIndex]
    const displayValue =
      value === null || value === undefined || isNaN(value)
        ? "0"
        : decimals === 0
        ? Math.round(value).toString()
        : value.toFixed(decimals)

    const category = w.globals.labels[dataPointIndex]
    const seriesName = w.config.series[seriesIndex].name || "Value"
    const isDistributed = w?.config?.plotOptions?.bar?.distributed === true
    const colorIndex = isDistributed ? dataPointIndex : seriesIndex
    const color = w.config.colors[colorIndex] || w.config.colors[0] || "#000"

    return `
      <div class="apexcharts-tooltip-title"
      style="background:#ECEFF1;font-size:12px;border-bottom:1px solid #ddd;padding:8px 10px;color:#000;">
        ${category}
      </div>
      <div style="padding:8px 10px;background:#fff;display:flex;align-items:center;">
        <span style="background:${color};width:10px;height:10px;border-radius:50%;margin-right:8px;"></span>
        <span style="font-size:12px;color:#000;margin-right:5px;">${seriesName}: </span>
        <span style="font-size:12px;font-weight:600;color:#000;">${displayValue}</span>
      </div>
    `
  }

  const options: ApexOptions = useMemo(() => {
    return {
    chart: {
      type: "bar",
      height,
      stacked,
      fontFamily,
      events: {
        mounted: () => addInfoIcon(),
        updated: () => addInfoIcon(),
        dataPointSelection: (event: any, chartContext: any, config: any) => {
          if (!tapTooltipOnTouch || !isTouchDevice || !chartRef.current) return

          const { seriesIndex, dataPointIndex } = config || {}
          if (seriesIndex == null || dataPointIndex == null || seriesIndex < 0 || dataPointIndex < 0) {
            return
          }

          const pointKey = `${seriesIndex}-${dataPointIndex}`
          const chartRect = chartRef.current.getBoundingClientRect()
          const x = typeof event?.clientX === "number" ? event.clientX - chartRect.left : chartRect.width / 2
          const y = typeof event?.clientY === "number" ? event.clientY - chartRect.top : chartRect.height / 2
          const series = chartContext?.w?.globals?.series || []
          const html = tooltipCustom
            ? tooltipCustom({ series, seriesIndex, dataPointIndex, w: chartContext?.w })
            : getTooltipHtml(series, seriesIndex, dataPointIndex, chartContext?.w)

          setTapTooltip((current) => ({
            visible: current.pointKey === pointKey ? !current.visible : true,
            x,
            y,
            html,
            pointKey
          }))
        }
      },
      toolbar: {
        show: showToolbar,
        export: exportFilename
          ? {
              csv: { filename: exportFilename },
              png: { filename: exportFilename },
              svg: { filename: exportFilename }
            }
          : undefined
      }
    },

    colors,

    series: safeSeries,

    plotOptions: {
      bar: {
        horizontal,
        columnWidth: `${isMobile ? 80 : columnWidth}%`,
        borderRadius: 0,
        ...plotOptionsBar
      }
    },

    grid: {
      show: showGrid,
      padding: {
        top: gridPadding?.top ?? 0,
        right: gridPadding?.right ?? 10,
        bottom: gridPadding?.bottom ?? 10,
        left: gridPadding?.left ?? 15
      }
    },

    dataLabels: {
      enabled: dataLabelsEnabled,
      offsetX: dataLabelsOffsetX,
      offsetY: dataLabelsOffsetY,
      style: {
        fontWeight: dataLabelsFontWeight,
        fontSize: dataLabelsFontSize,
        colors: dataLabelsColor ? dataLabelsColor : ["#000"]
      },

      formatter: (val, opts) => {
        const numVal = Number(val)

        if (!isFinite(numVal)) return ""

        if (horizontal) {
          if (
            categoryLabelsInside &&
            opts?.dataPointIndex !== undefined
          ) {
            return categories[opts.dataPointIndex] || ""
          }
          return decimals === 0
          ? Math.round(numVal).toString()
          : numVal.toFixed(decimals)
        }

        if (numVal === 0) {
          return `0${dataLabelSuffix || "%"}`
        }

        if (!showlabelsForBars) return ""

        return decimals === 0
          ? `${Math.round(numVal)}${dataLabelSuffix || "%"}`
          : `${numVal.toFixed(decimals)}${dataLabelSuffix || "%"}`
      }
    },

    xaxis: {
      categories,
      min: xAxisMin,
      max: xAxisMax,
      tickAmount: xAxisTickAmount,

      labels: {
        // When xAxisReserveSpace is set, keep the label track rendered but make
        // hidden labels transparent + zero-height so plot spacing is preserved
        // (opt-in; default keeps the original show/hide behavior untouched).
        show: xAxisReserveSpace ? true : xAxisShow,
        rotate: xAxisLabelRotate,
        rotateAlways: xAxisLabelRotateAlways,
        hideOverlappingLabels: false,
        trim: false,
        minHeight: xAxisReserveSpace && !xAxisShow ? 0 : xAxisLabelMinHeight,
        maxHeight: xAxisReserveSpace && !xAxisShow ? 0 : (xAxisLabelMaxHeight || 120),
        offsetX: xAxisLabelOffsetX,
        offsetY: xAxisLabelOffsetY,
        formatter: xAxisReserveSpace && !xAxisShow
          ? () => ""
          : (xAxisMultiLine
              ? (val: string) => val?.split("\n")
              : undefined),
        style: {
          fontSize: '12.5px',
          colors: xAxisReserveSpace && !xAxisShow ? 'transparent' : 'Black',
          fontWeight: 'normal',
          fontFamily,
        }
      },

      axisBorder: {
        show: showXAxisBorder
      },

      axisTicks: {
        show: showXAxisBorder
      },

      title: {
        text: xAxisLabel,
        style: {
          fontSize: '15px',
          color: 'Black',
          fontWeight: 'normal',
          fontFamily,
        }
      }
    },

    yaxis: {
      show: yAxisShow,
      min: yAxisMin,
      max: yAxisMax,
      tickAmount: yAxisTickAmount,
      title: {
        text: yAxisLabel,
        style: {
          fontSize: '15px',
          color: 'Black',
          fontWeight: 'normal',
          fontFamily,
        }
      },

      labels: {
        minWidth: yAxisWidth,
        maxWidth: yAxisWidth ? yAxisWidth + 50 : undefined,
        offsetX: yAxisLabelOffsetX,

        formatter: (val) => {
          if (horizontal && categoryLabelsInside) {
            return ""
          }

          if (horizontal && typeof val === "string") {
            return val
          }

          const num = Number(val)
          if (!isFinite(num)) return ""

          return yAxisDecimals
            ? num.toFixed(yAxisDecimals)
            : Math.round(num).toString()
        },
        style: {
          fontSize: '15px',
          colors: 'Black',
          fontWeight: 'normal',
          fontFamily,
        }
      }
    },

    legend: {
      show: showLegend ?? series.length > 0,
      showForSingleSeries: showLegendForSingleSeries,
      position: legendPosition,
      horizontalAlign: legendAlign,

      floating: legendFloating,
      offsetX: legendOffsetX,
      offsetY: legendOffsetY,

      fontSize: legendFontSize,
      fontWeight: legendFontWeight,

      labels: {
        colors: legendFontColor
      },

      markers: {
        size: 6,
        strokeWidth: 0,
        shape: "circle",
      }
    },

    tooltip: {
      enabled: !(tapTooltipOnTouch && isTouchDevice),
      shared: false,
      followCursor: !isMobile,
      intersect: true,
      custom: ({ series: s, seriesIndex, dataPointIndex, w }: { series: number[][]; seriesIndex: number; dataPointIndex: number; w: any }) => {
        if (tooltipCustom) return tooltipCustom({ series: s, seriesIndex, dataPointIndex, w })
        return getTooltipHtml(s, seriesIndex, dataPointIndex, w)
      }
    },

    responsive: [
      {
        breakpoint: 768,
        options: {
          xaxis: {
            labels: {
              style: {
                fontSize: "12px"
              }
            }
          }
        }
      },
      {
        breakpoint: 480,
        options: {
          xaxis: {
            labels: {
              style: {
                fontSize: "10px"
              },
              rotate: -45
            }
          }
        }
      }
    ],

    annotations: goalLine
      ? {
          [horizontal ? "xaxis" : "yaxis"]: [
            {
              [horizontal ? "x" : "y"]: goalLine,
              borderColor: "#009E73",
              borderWidth: 3,
              label: {
                text: goalLabel,
                borderColor: "#009E73",
                offsetX: goalLabelOffsetX ?? 0,
                offsetY: goalLabelOffsetY,
                ...(goalLabelPosition ? { position: goalLabelPosition } : {}),
                ...(goalLabelOrientation ? { orientation: goalLabelOrientation } : {}),
                style: {
                  background: "#009E73",
                  color: "#fff",
                  fontSize: "14px",
                  fontFamily,
                }
              }
            }
          ]
        }
      : undefined,

    noData: {
      text: noDataMessage,
      style: {
        color: zeroStateTextColor
      }
    }
  };
  }, [categories, columnWidth, dataLabelSuffix, dataLabelsColor, dataLabelsEnabled, dataLabelsFontSize, dataLabelsFontWeight, dataLabelsOffsetX, dataLabelsOffsetY, decimals, exportFilename, fontFamily, goalLabel, goalLabelOffsetX, goalLabelOffsetY, goalLabelOrientation, goalLabelPosition, goalLine, gridPadding, horizontal, isMobile, isTouchDevice, legendAlign, legendFloating, legendFontColor, legendFontSize, legendFontWeight, legendOffsetX, legendOffsetY, legendPosition, noDataMessage, plotOptionsBar, safeSeries, series, showGrid, showLegend, showLegendForSingleSeries, showToolbar, showXAxisBorder, showlabelsForBars, stacked, tapTooltipOnTouch, title, tooltipCounts, tooltipCustom, xAxisHideOverlappingLabels, xAxisLabel, xAxisLabelMinHeight, xAxisLabelMaxHeight, xAxisLabelOffsetX, xAxisLabelOffsetY, xAxisLabelRotate, xAxisLabelRotateAlways, xAxisMax, xAxisMin, xAxisMultiLine, xAxisShow, xAxisTickAmount, xAxisTrim, yAxisDecimals, yAxisLabel, yAxisLabelOffsetX, yAxisMax, yAxisMin, yAxisShow, yAxisTickAmount, yAxisWidth, zeroStateTextColor, categoryLabelsInside])

  useEffect(() => {
    if (!chartRef.current) return
    chartInstance.current = new ApexCharts(chartRef.current, options)
    chartInstance.current.render()

    return () => {
      chartInstance.current?.destroy()
      chartInstance.current = null
    }
  }, [])

  useEffect(() => {
    if (!chartInstance.current) return
    chartInstance.current.updateOptions(options, true, true)
    chartInstance.current.updateSeries(options.series || [])
  }, [options])

  const addInfoIcon = () => {
    if (!chartRef.current || series.length <= 1) return
    const legendContainer =
      chartRef.current.querySelector(".apexcharts-legend")

    if (!legendContainer) return

    if (legendContainer.querySelector(".legend-info-icon")) return

    const icon = document.createElement("span")
    icon.className = "legend-info-icon"

    icon.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="#808080"/>
        <text x="12" y="17" font-size="14" fill="white" text-anchor="middle">i</text>
      </svg>
      <div class="legend-info-tooltip">
        Click on a legend item to hide or show that data series on the chart
      </div>
    `

    legendContainer.appendChild(icon)
  }

  return (
    <div className="bar-chart-container" style={{ position: "relative" }}>
      {title && <div className="bar-chart-title">{title}</div>}

      <div ref={chartRef} className="bar-chart" />

      {tapTooltip.visible && (
        <div
          className="custom-bar-tooltip visible"
          style={{
            left: tapTooltip.x,
            top: tapTooltip.y
          }}
          dangerouslySetInnerHTML={{ __html: tapTooltip.html }}
        />
      )}

      {isLoading && (
        <div className="chart-loading-overlay">
          <div>Loading...</div>
        </div>
      )}
    </div>
  )
})

BarChart.displayName = "BarChart"

export default BarChart
